import type { UploadPartPresign } from '@/services/file-multipart.service'
import { multipartErrorCode } from './multipart-errors'

/**
 * The bounded control-plane backoff for a presign the backend refused because
 * the signer credential window was too short.
 *
 * The delays are this client's own: the closed record fixes the response and
 * the exhaustion behavior — retry with a bounded backoff, then pause the same
 * session — but not the intervals, unlike the finishing poll, whose schedule is
 * fixed. Two retries at these spacings cover the case the `503` exists for: a
 * credential that is about to be rotated and will be sound moments later.
 */
export const PRESIGN_RETRY_DELAYS_MS = [1000, 2000]

const realSleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Requests presigned parts, retrying ONLY the credential-window refusal.
 *
 * Every other failure is surfaced immediately: this backoff exists for a
 * transient signer condition, and retrying an authorization or contract error
 * through it would only delay the real answer. On exhaustion the original error
 * is rethrown, so the caller pauses the same session rather than creating a new
 * one — nothing here mutates session state.
 */
export async function requestPresignWithBackoff(
	request: (partNumbers: number[]) => Promise<UploadPartPresign[]>,
	partNumbers: number[],
	sleep: (ms: number) => Promise<unknown> = realSleep,
): Promise<UploadPartPresign[]> {
	for (let attempt = 0; ; attempt++) {
		try {
			return await request(partNumbers)
		} catch (error) {
			const retryable =
				multipartErrorCode(error) === 'PRESIGN_CREDENTIAL_WINDOW_TOO_SHORT' && attempt < PRESIGN_RETRY_DELAYS_MS.length
			if (!retryable) throw error
			await sleep(PRESIGN_RETRY_DELAYS_MS[attempt])
		}
	}
}
